import type { BiomeId } from './world';

export type AnimalSpecies =
  'cow' | 'goat' | 'reindeer' | 'sheep' | 'pig' | 'donkey' | 'martian' | 'mooncow';

export interface AnimalProfile {
  species: AnimalSpecies;
  singular: string;
  plural: string;
  emoji: string;
  call: 'moo' | 'bleat' | 'grunt' | 'baa' | 'oink' | 'bray' | 'warble' | 'lowmoo';
}

export const ANIMAL_PROFILES: Record<AnimalSpecies, AnimalProfile> = {
  cow: { species: 'cow', singular: 'cow', plural: 'cows', emoji: '🐄', call: 'moo' },
  goat: { species: 'goat', singular: 'goat', plural: 'goats', emoji: '🐐', call: 'bleat' },
  reindeer: { species: 'reindeer', singular: 'reindeer', plural: 'reindeer', emoji: '🦌', call: 'grunt' },
  sheep: { species: 'sheep', singular: 'sheep', plural: 'sheep', emoji: '🐑', call: 'baa' },
  pig: { species: 'pig', singular: 'pig', plural: 'pigs', emoji: '🐖', call: 'oink' },
  // The Donkey Farm's whole herd. Donkeys are not a stray anywhere: they are a
  // place on the map, and finding them is the point.
  donkey: { species: 'donkey', singular: 'donkey', plural: 'donkeys', emoji: '🫏', call: 'bray' },
  // Off-world stock. The greys graze Cydonia; the moon cows are the ones from
  // the rhyme, still up there, still chewing.
  martian: { species: 'martian', singular: 'grey', plural: 'greys', emoji: '👽', call: 'warble' },
  mooncow: { species: 'mooncow', singular: 'moon cow', plural: 'moon cows', emoji: '🐄', call: 'lowmoo' },
};

/**
 * A land's herd is no longer one species. `primary` is what the land is known
 * for — it names the quota in the HUD, the opening title card and the hints —
 * and `stray` is the minority that shares the pasture with it, so a field
 * reads as livestock rather than as a cloned prop. Sheep stray almost
 * everywhere on purpose: the baa is the sound of the game's British farmland.
 */
export interface HerdSpec {
  primary: AnimalSpecies;
  stray: AnimalSpecies;
  /** Share of spawns that come up as the stray species. */
  strayChance: number;
}

export const BIOME_HERDS: Record<BiomeId, HerdSpec> = {
  valley: { primary: 'cow', stray: 'sheep', strayChance: 0.3 },
  mesa: { primary: 'goat', stray: 'cow', strayChance: 0.18 },
  snow: { primary: 'reindeer', stray: 'sheep', strayChance: 0.22 },
  moor: { primary: 'sheep', stray: 'goat', strayChance: 0.24 },
  town: { primary: 'pig', stray: 'sheep', strayChance: 0.26 },
  mars: { primary: 'martian', stray: 'mooncow', strayChance: 0.2 },
  moon: { primary: 'mooncow', stray: 'martian', strayChance: 0.24 },
};

/** The species a land is named for. Kept as its own map because the HUD, the
 *  title card and the hint lines all want the headline animal, not a roll. */
export const BIOME_ANIMALS: Record<BiomeId, AnimalSpecies> = Object.fromEntries(
  (Object.entries(BIOME_HERDS) as [BiomeId, HerdSpec][]).map(([id, herd]) => [id, herd.primary]),
) as Record<BiomeId, AnimalSpecies>;

export function animalForBiome(biome: BiomeId): AnimalProfile {
  return ANIMAL_PROFILES[BIOME_ANIMALS[biome]];
}

/** Roll one spawn's species for a land. Pure — `rand` is injectable for tests. */
export function rollHerdSpecies(biome: BiomeId, rand: () => number = Math.random): AnimalSpecies {
  const herd = BIOME_HERDS[biome];
  return rand() < herd.strayChance ? herd.stray : herd.primary;
}

export function animalName(species: AnimalSpecies, count = 1): string {
  const profile = ANIMAL_PROFILES[species];
  return count === 1 ? profile.singular : profile.plural;
}
